import React from 'react';
import {
  HandleCheckboxEvent,
  Text,
  TokenTextAppearance,
  TokenTextColor,
  TextTag,
  styled,
  InputAppearanceValue
} from '@volkswagen-onehub/components-core';
import { getCommonProps } from '@volkswagen-onehub/components-core/dist/helpers/common-props';
import { CheckboxCheckmark } from '@volkswagen-onehub/icons-core';
import { Check } from '../../components/Check';

type ColourSwatchProps = {
  appearance?: InputAppearanceValue;
  textAppearance?: TokenTextAppearance;
  message?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  label?: React.ReactNode | string;
  name?: string;
  onBlur?: HandleCheckboxEvent;
  onChange?: HandleCheckboxEvent;
  onClick?: HandleCheckboxEvent;
  onFocus?: HandleCheckboxEvent;
  onLabelMouseDown?: React.MouseEventHandler<HTMLLabelElement>;
  required?: boolean;
  tabIndex?: -1 | 0;
  color: string;
  value: string;
  innerRef?: React.Ref<HTMLInputElement>;
};

type ColourSwatchInnerProps = ColourSwatchProps & {
  handleChange?: (e: React.SyntheticEvent<HTMLInputElement, Event>) => void;
  handleBlur?: (e: React.SyntheticEvent<HTMLInputElement, Event>) => void;
  handleFocus?: (e: React.SyntheticEvent<HTMLInputElement, Event>) => void;
  handleClick?: (e: React.SyntheticEvent<HTMLInputElement, Event>) => void;
};

const StyledColourSwatch = styled.div<ColourSwatchProps>`
  width: 48px;
  height: 48px;
  background-color: ${({ color }) => color};
  border-radius: 50%;
`;

const StyledCheckWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(4px, -4px);
  opacity: 0;
`;

const StyledLabel = styled.label`
  position: relative;
  color: ${(props) => props.theme.colors.content.active};
  cursor: pointer;
`;

type CheckboxWrapperProps = {
  textAppearance: TokenTextAppearance;
};

const StyledCheckboxWrapper = styled.span<CheckboxWrapperProps>`
  display: block;
  position: relative;
  margin: 12px 0 0;
`;

const StyledCheckboxCheckmark = styled(CheckboxCheckmark)`
  position: absolute;
  opacity: 0;
  display: flex;
  align-items: center;
`;

const StyledFocusWrapper = styled.span`
  min-width: 60px;
  width: min-content;
  position: relative;
  display: grid;
  justify-items: center;
  align-items: center;
  &:hover {
    ${StyledCheckboxWrapper} {
      &::before {
        content: '';
        position: absolute;
        top: -${(props) => props.theme.size.static150};
        left: -${(props) => props.theme.size.static150};
        height: ${(props) => `calc(100% + ${props.theme.size.static250})`};
        width: ${(props) => `calc(100% + ${props.theme.size.static250})`};
        background-color: ${(props) =>
          props.theme.colors.hoverIndicator};
        transition: ${(props) => props.theme.animation.duration.duration400};
        border-radius: 50%;
      }
    }
  }
`;

const StyledErrorText = styled.span`
  display: block;
  color: ${(props) => props.theme.colors.signal.error};
  font-size: ${(props) => props.theme.fonts.copy[100].fontSize};
`;

const StyledInput = styled.input`
  position: absolute;
  opacity: 0;
  outline: 0;
  user-select: none;
  pointer-events: none;
  :checked {
    + ${StyledFocusWrapper} {
      color: ${(props) =>props.theme.colors.content.active};
      ${StyledCheckboxWrapper} {
        ${StyledCheckWrapper} {
          opacity: 1;
        }
      }
    }
  }
  :focus {
    + ${StyledFocusWrapper} {
      ${StyledCheckboxWrapper} {
        &::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          height: ${(props) => `calc(100% + ${props.theme.size.static150})`};
          width: ${(props) => `calc(100% + ${props.theme.size.static150})`};
          background-color: transparent;
          transition: ${(props) => props.theme.animation.duration.duration400};
          border-radius: 50%;
          z-index: -1;
          box-shadow: #4CC7F4 0px 0px 0px 2px inset;
        }
      }
      ${StyledCheckboxCheckmark} {
        color: ${({theme}) => theme.colors.content.inverted};
      }
    }
  }
  &&&:disabled {
    + ${StyledFocusWrapper} {
      color: ${(props) => props.theme.colors.content.disabled};
      cursor: not-allowed;
    }
  }
`;

const noop = () => undefined;

const ColourSwatchInner = (props: ColourSwatchInnerProps) => {
  const {
    checked,
    defaultChecked,
    disabled,
    label = "",
    name,
    value,
    required,
    appearance,
    message,
    tabIndex,
    textAppearance = TokenTextAppearance.copy200,
    onLabelMouseDown,
    onChange = noop,
    onBlur = noop,
    onFocus = noop,
    onClick = noop,
    innerRef,
    color
  } = props;

  const commonProps = getCommonProps(props);

  return (
    <StyledLabel onMouseDown={onLabelMouseDown} {...commonProps}>
      <StyledInput
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        name={name}
        onBlur={(e : React.SyntheticEvent<HTMLInputElement>) => onBlur(e, props)}
        onChange={(e : React.SyntheticEvent<HTMLInputElement>) => onChange(e, props)}
        onClick={(e : React.SyntheticEvent<HTMLInputElement>) => onClick(e, props)}
        onFocus={(e : React.SyntheticEvent<HTMLInputElement>) => onFocus(e, props)}
        type={'checkbox'}
        value={value}
        required={required}
        tabIndex={tabIndex}
        ref={innerRef}
      />
      <StyledFocusWrapper>
        <StyledCheckboxWrapper
          textAppearance={textAppearance}
          aria-hidden={true}
        >
          <StyledColourSwatch
            color={color}
            value={value}
          />
          <StyledCheckWrapper>
            <Check size="medium"/>
          </StyledCheckWrapper>
        </StyledCheckboxWrapper>
        <span>
          { label &&
            typeof label === 'string'
            ? <Text
                tag={TextTag.span}
                color={TokenTextColor.inherit}
                appearance={textAppearance}
              >
                { label }
              </Text>
            : label
          }
        </span>
      </StyledFocusWrapper>
      <StyledErrorText aria-live="assertive" aria-relevant="all">
        { appearance === "error" && message }
      </StyledErrorText>
    </StyledLabel>
  );
};

export const ColourSwatch = React.forwardRef<HTMLInputElement, ColourSwatchInnerProps>(
  (props, ref) => (
    <ColourSwatchInner {...props} innerRef={ref} />
  )
);
